import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAddressComponent,
  type OnyxColor,
  OnyxDataWidgetComponent,
} from '@onyx/angular';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { RouteHelper } from '../../../../../../../common/helpers/route.helper';
import type { TelematicsTechnicalRoutePoint } from '../../../../../../../common/interfaces/telematics/telematics-route-point';
import { DurationPipe } from '../../../../../../../common/pipes/duration.pipe';
import { PointOfInterestHelper } from '../../../../../../management-panel/points-of-interest/common/helpers/point-of-interest.helper';
import { SwapModalHelper } from '../../../../../../map/common/helpers/swap/swap-modal.helper';
import { TelematicsHelper } from '../../../../../../map/common/helpers/telematics.helper';
import { OrderPointCategory } from '../../../../../common/enums/order-point-category';
import type { Order } from '../../../../../common/interfaces/order';
import type { OrderPoint } from '../../../../../common/interfaces/order-point';

@Component({
  selector: 'app-order-modal-realization-point',
  imports: [
    OnyxDataWidgetComponent,
    OnyxAddressComponent,
    TranslatePipe,
    DurationPipe,
  ],
  templateUrl: './order-modal-realization-point.component.html',
  styleUrl: './order-modal-realization-point.component.scss',
})
export class OrderModalRealizationPointComponent {
  private readonly pointOfInterestHelper = inject(PointOfInterestHelper);
  private readonly swapModalHelper = inject(SwapModalHelper);

  public readonly order = input.required<Order>();
  public readonly currentPoint = input.required<OrderPoint>();
  public readonly currentSwapPoint =
    input.required<TelematicsTechnicalRoutePoint | null>();
  public readonly color = input.required<OnyxColor | null>();

  protected readonly DictionaryCode = DictionaryCode;

  protected isClickable = computed(
    () =>
      !!this.currentSwapPoint() ||
      RouteHelper.isHandlingPoint(this.currentPoint()),
  );
  protected icon = computed(() =>
    TelematicsHelper.getPointIcon(this.currentPoint()),
  );

  protected address = computed(() => {
    const point =
      this.currentSwapPoint()?.technicalPoint ?? this.currentPoint();
    return point.category === OrderPointCategory.TRANSIT
      ? point.startAddress
      : point.address;
  });
  protected name = computed(() => {
    const address = this.address();
    const point =
      this.currentSwapPoint()?.technicalPoint ?? this.currentPoint();

    if ('poiName' in address) return address.poiName;
    if ('name' in point) return point.name;
    return '-';
  });
  protected pointLabel = computed(() => {
    const point =
      this.currentSwapPoint()?.technicalPoint ?? this.currentPoint();
    if (RouteHelper.isTechnicalPoint(point)) {
      return `${DictionaryCode.TECHNICAL_POINT_CATEGORY}.${point.category}`;
    }

    return `${DictionaryCode.ORDER_POINT_TYPE}.${point.type}`;
  });
  protected serviceTimeLabel = computed(
    () =>
      `forms.serviceTime.${RouteHelper.getType(this.currentSwapPoint()?.technicalPoint ?? this.currentPoint())}`,
  );

  protected openPointModal(): void {
    const swapPoint = this.currentSwapPoint();
    if (swapPoint) {
      const swapUuid = RouteHelper.getSwapData(swapPoint)?.uuid;
      if (!swapUuid) return;

      this.swapModalHelper.openModal(
        swapUuid,
        this.order().assignedData?.vehicle?.uuid ?? null,
      );
      return;
    }

    const address = this.address();
    const poiUuid = 'poiUuid' in address ? address.poiUuid : null;
    if (poiUuid) this.pointOfInterestHelper.openModal(poiUuid);
  }
}
